/**
 * Footsteps for everyone in earshot: the ranger, every soldier within 60 m (so a patrol is heard
 * long before the 36 x 28 m view shows it - 01 §10), captives in tow, and a minitank's tracks.
 * A footfall is a stride boundary crossed by the sim's own `travelled` counter, so the rhythm
 * follows the real gait at any frame rate. Nothing is allocated per frame.
 */

import { isDog } from '../../sim/enemies/specials/kinds';
import type { World } from '../../sim/types';
import {
  ENEMY_STEP_GAIN, enemyGait, footfall, type Gait, GAIT_GAIN, GAIT_RATE, rangerGait, STRIDE, TREAD_STRIDE,
} from '../mix';
import { CRAWL_ID, STEP_ID } from '../sfx';
import { SPATIAL } from '../spatial';
import type { SurfaceLookup } from '../surface';
import type { Voices } from './voices';

/** Captive ids come from the mission file and may collide with enemy ids: keep them apart in the map. */
const CAPTIVE_KEY = 1_000_000;
const RANGER_KEY = -1;
const CAPTIVE_STEP_GAIN = 0.8;

export class Steps {
  /** `travelled` as last seen, by walker. */
  private readonly seen = new Map<number, number>();
  private surface: SurfaceLookup | null = null;

  constructor(private readonly voices: Voices) {}

  begin(surface: SurfaceLookup): void {
    this.surface = surface;
    this.seen.clear();
  }

  end(): void {
    this.surface = null;
    this.seen.clear();
  }

  private walker(key: number, travelled: number, x: number, z: number, gait: Gait, gain: number): void {
    const before = this.seen.get(key);
    this.seen.set(key, travelled);
    if (before === undefined || !this.surface || !footfall(before, travelled, STRIDE[gait])) return;
    const ground = this.surface(x, z);
    const id = gait === 'crawl' ? CRAWL_ID[ground] : STEP_ID[ground];
    this.voices.playAt(id, x, z, gain * GAIT_GAIN[gait], GAIT_RATE[gait]);
  }

  update(world: World, lx: number, lz: number): void {
    if (world.insertion.phase !== 'ground') return;
    const r = world.ranger;
    const onFoot = r.status === 'active' || r.status === 'downed';
    if (onFoot) this.walker(RANGER_KEY, r.travelled, r.x, r.z, rangerGait(r), 1);

    const reach = SPATIAL.maxDistance * SPATIAL.maxDistance;
    for (const e of world.enemies) {
      // ROUND8: a dog's pads make no footfall to hear over its handler's boots - and a boot's would be wrong.
      if (!e.moving || e.state === 'dead' || e.kind === 'miniBunker' || isDog(e.kind)) continue;
      if ((e.x - lx) * (e.x - lx) + (e.z - lz) * (e.z - lz) > reach) continue;
      if (e.kind === 'minitank') {
        const before = this.seen.get(e.id);
        this.seen.set(e.id, e.travelled);
        if (before !== undefined && footfall(before, e.travelled, TREAD_STRIDE)) this.voices.playAt('tread', e.x, e.z);
        continue;
      }
      this.walker(e.id, e.travelled, e.x, e.z, enemyGait(e), ENEMY_STEP_GAIN);
    }
    for (const c of world.objective.captives) {
      if (!c.moving || c.state !== 'following') continue;
      this.walker(CAPTIVE_KEY + c.id, c.travelled, c.x, c.z, 'walk', CAPTIVE_STEP_GAIN);
    }
  }
}
